import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { useTheme } from '../contexts/ThemeContext';

interface FirstLoginEULAAcceptanceProps {
  authToken: string;
  onComplete: () => void;
  onSkip?: () => void;
}

const FirstLoginEULAAcceptance: React.FC<FirstLoginEULAAcceptanceProps> = ({
  authToken,
  onComplete,
  onSkip
}) => {
  const { isDarkMode } = useTheme();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eulaContent, setEulaContent] = useState('');

  useEffect(() => {
    const fetchEULA = async () => {
      try {
        const response = await fetch('/end-user-agreement.md');
        const text = await response.text();
        setEulaContent(text);
      } catch (error) {
        console.error('Failed to load EULA:', error);
        setEulaContent('End User Agreement content could not be loaded. Please contact support.');
      }
    };
    
    fetchEULA();
  }, []);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px tolerance
    
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!isAccepted || !hasScrolledToBottom) return;
    
    setIsSubmitting(true);
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/auth/accept-eula`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      
      if (response.ok) {
        onComplete();
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || 'Unknown error';
        } catch (parseError) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
        alert(`Failed to accept EULA: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error accepting EULA:', error);
      alert('Failed to accept EULA. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatEULAContent = (content: string) => {
    // Simple markdown-like formatting for display
    return content
      .replace(/^#\s+(.+)$/gm, '<h1 class="text-2xl font-bold mb-4">$1</h1>')
      .replace(/^##\s+(.+)$/gm, '<h2 class="text-xl font-semibold mb-3">$1</h2>')
      .replace(/^###\s+(.+)$/gm, '<h3 class="text-lg font-medium mb-2">$1</h3>')
      .replace(/^\*\s+(.+)$/gm, '<li class="ml-4">• $1</li>')
      .replace(/^([^<\n].+)$/gm, '<p class="mb-3">$1</p>')
      .replace(/\n\n/g, '\n');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className={`max-w-4xl w-full mx-auto p-8 rounded-lg shadow-lg ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            End User License Agreement
          </h1>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Please read and accept the terms below to continue using Risk Ninja
          </p>
        </div>

        <div className={`border rounded-lg mb-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <div 
            className={`h-96 overflow-y-auto p-6 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-800'}`}
            onScroll={handleScroll}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatEULAContent(eulaContent)) }}
              className="prose prose-sm max-w-none"
            />
          </div>
          
          {!hasScrolledToBottom && (
            <div className={`p-3 text-center border-t ${
              isDarkMode ? 'border-gray-600 bg-yellow-900 text-yellow-200' : 'border-gray-300 bg-yellow-50 text-yellow-800'
            }`}>
              <p className="text-sm">Please scroll to the bottom to continue</p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className={`flex items-center space-x-3 cursor-pointer ${
            !hasScrolledToBottom ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            <input
              type="checkbox"
              checked={isAccepted}
              onChange={(e) => setIsAccepted(e.target.checked)}
              disabled={!hasScrolledToBottom}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className={`text-lg ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              I have read and agree to the End User License Agreement
            </span>
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleAccept}
            disabled={!isAccepted || !hasScrolledToBottom || isSubmitting}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              !isAccepted || !hasScrolledToBottom || isSubmitting
                ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isSubmitting ? 'Accepting...' : 'I Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginEULAAcceptance;